import { CLASS_NAMES, EVENTS, SELECTORS } from '@bs/constants';
import { MessageBus } from '@bs/utils';

/**
 * A class for the Desktop Navigation Dropdown module
 */
export default class DesktopMenuDropdown extends MessageBus {
  /**
   * Constructor function of the DesktopMenuDropdown class.
   * @param {HTMLElement} element - REQUIRED - the module's container
   */
  constructor(element) {
    super();
    this.element = element;

    // Initialize the view
    this.init();
  }

  /**
   * Initializes the view by calling the functions to
   * create DOM references, setup event handlers and
   * then create the event listeners
   * @returns {object} DesktopNavDropdown A reference to the current instance of the class
   * @chainable
   */
  init() {
    this.cacheDomReferences().setupHandlers().enable();
    return this;
  }

  /**
   * Cache DOM References
   *
   * Find all necessary DOM elements used in the view and cache them
   * @returns {object} DesktopNavDropdown A reference to the current instance of the class
   * @chainable
   */
  cacheDomReferences() {
    this.menu = this.element.querySelector(CLASS_NAMES.NAV_DESKTOP);

    // Menu items have IDs: menu-item-1, menu-item-2, ...
    this.menuItems = this.menu
      ? this.menu.querySelectorAll(`[id^="${SELECTORS.MENU_ITEM}"]`)
      : [];

    // Dropdowns have IDs: dropdown-menu-1, dropdown-menu-2, ...
    this.dropdowns = this.menu
      ? this.menu.querySelectorAll(`[id^="${SELECTORS.DROPDOWN_MENU}"]`)
      : [];

    // Track currently open index (string or null)
    this.activeIndex = null;

    return this;
  }

  /**
   * Bind event handlers with the proper context of `this`.
   * @returns {object} DesktopNavDropdown A reference to the current instance of the class
   * @chainable
   */
  setupHandlers() {
    this.onMenuItemClickHandler = this.onMenuItemClick.bind(this);
    this.onDocumentClickHandler = this.onDocumentClick.bind(this);
    this.onKeyDownHandler = this.onKeyDown.bind(this);

    return this;
  }

  /**
   * Create event handlers to enable interaction with view
   * @returns {object} DesktopNavDropdown A reference to the current instance of the class
   * @chainable
   */
  enable() {
    if (this.menuItems && this.menuItems.length) {
      this.menuItems.forEach((item) => {
        item.addEventListener(EVENTS.CLICK, this.onMenuItemClickHandler);
      });
    }

    // Close when clicking outside the menu/dropdowns
    document.addEventListener(EVENTS.CLICK, this.onDocumentClickHandler);

    // Close on Escape
    document.addEventListener(EVENTS.KEYDOWN, this.onKeyDownHandler);

    return this;
  }

  /* eslint-disable class-methods-use-this */
  /**
   * Extracts the index from a menu item id, e.g. "menu-item-3" -> "3"
   * @param {string} id - element id
   * @returns {string|null} index string or null
   */
  getIndexFromMenuId(id) {
    if (!id) return null;
    const match = id.match(/^menu-item-(\d+)$/);
    return match ? match[1] : null;
  }
  /* eslint-enable class-methods-use-this */

  /**
   * Returns the dropdown element for a given index, e.g. "3" -> #dropdown-menu-3
   * @param {string} index - index string
   * @returns {HTMLElement|null} dropdown element or null
   */
  getDropdownByIndex(index) {
    if (!index) return null;
    return this.menu.querySelector(`#${SELECTORS.DROPDOWN_MENU}${index}`);
  }

  /**
   * Returns the menu item element for a given index, e.g. "3" -> #menu-item-3
   * @param {string} index - index string
   * @returns {HTMLElement|null} menu item element or null
   */
  getMenuItemByIndex(index) {
    if (!index) return null;
    return this.menu.querySelector(`#${SELECTORS.MENU_ITEM}${index}`);
  }

  /**
   * Closes all open dropdowns and clears active states.
   * @returns {object} DesktopNavDropdown A reference to the current instance of the class
   * @chainable
   */
  closeAll() {
    if (this.menuItems && this.menuItems.length) {
      this.menuItems.forEach((item) =>
        item.classList.remove(CLASS_NAMES.ACTIVE)
      );
    }

    if (this.dropdowns && this.dropdowns.length) {
      this.dropdowns.forEach((dd) => dd.classList.remove(CLASS_NAMES.ACTIVE));
    }

    this.activeIndex = null;
    return this;
  }

  /**
   * Opens the dropdown for the given index and marks the corresponding menu item active.
   * Also closes any other open dropdown first.
   * @param {string} index - index string
   * @returns {object} DesktopNavDropdown A reference to the current instance of the class
   * @chainable
   */
  openIndex(index) {
    if (!index) return this;

    // Accordion behavior: only one open at a time
    this.closeAll();

    const menuItem = this.getMenuItemByIndex(index);
    const dropdown = this.getDropdownByIndex(index);

    // Some menu items may not have children => dropdown may not exist
    if (!dropdown || !menuItem) {
      this.activeIndex = null;
      return this;
    }

    menuItem.classList.add(CLASS_NAMES.ACTIVE);
    dropdown.classList.add(CLASS_NAMES.ACTIVE);

    this.activeIndex = index;
    return this;
  }

  /**
   * Toggles a dropdown for the given index.
   * If already open, it closes; otherwise it opens and closes others.
   * @param {string} index - index string
   * @returns {object} DesktopNavDropdown A reference to the current instance of the class
   * @chainable
   */
  toggleIndex(index) {
    if (!index) return this;

    // If clicking the currently active item -> close it
    if (this.activeIndex === index) {
      this.closeAll();
    } else {
      this.openIndex(index);
    }

    return this;
  }

  /**
   * Handles click events on menu items and applies the required open/close behavior.
   * - Click same item: toggle its dropdown
   * - Click different item: close any open dropdown and open the clicked one (if it has a dropdown)
   * @param {Event} event - the event object
   * @returns {object} DesktopNavDropdown A reference to the current instance of the class
   * @chainable
   */
  onMenuItemClick(event) {
    const item = event.currentTarget;

    // Only intercept click if this item has a dropdown (children)
    const index = this.getIndexFromMenuId(item.id);
    const dropdown = this.getDropdownByIndex(index);

    // If there is no dropdown for this menu item, allow navigation (do not preventDefault)
    if (!dropdown) return this;

    // Prevent navigation since we want toggle behavior for items with children
    event.preventDefault();
    event.stopPropagation();

    this.toggleIndex(index);
    return this;
  }

  /**
   * Closes any open dropdown if clicking outside the desktop menu/dropdowns.
   * @param {Event} event - the event object
   * @returns {object} DesktopNavDropdown A reference to the current instance of the class
   * @chainable
   */
  onDocumentClick(event) {
    // If click is inside the menu container, do nothing
    if (this.menu && this.menu.contains(event.target)) return this;

    // Otherwise close everything
    this.closeAll();
    return this;
  }

  /**
   * Closes any open dropdown on Escape key.
   * @param {KeyboardEvent} event - the event object
   * @returns {object} DesktopNavDropdown A reference to the current instance of the class
   * @chainable
   */
  onKeyDown(event) {
    if (event.key === EVENTS.ESCAPE) {
      this.closeAll();
    }
    return this;
  }
}
